<template>
  <div class="container-fluid mt-5">
    <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
      <template slot="header">
        <div class="row" v-if="!!transfer.id">
          <div class="col-8">
            <h3 class="mb-0 view-header-buttons">
              {{ transfer.seq_number ?? transfer.code }}
              | {{ transfer.sourceEstablishment.company_name }} =>
              {{ transfer.destinationEstablishment.company_name }}
              <base-button
                class="mr-2"
                type="primary"
                size="sm"
                @click="goBack"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>
              <br/><br/>
              <base-button
                v-if="$currentUserCan($permissions.PERM_EDIT_ESTABLISHMENT_INTERNAL_INVOICES) && transfer.status === 'DRAFT'"
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :disabled="loading"
                @click="goToEdit"
              >
                <i class="fas fa-edit"></i>
                {{ $t("COMMON.EDIT") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'DRAFT' && transfer.items.length > 0"
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :loading="loading"
                @click="validate"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("COMMON.SUBMIT") }}
              </base-button>
              <base-button
                v-if="
                  transfer.status === 'DRAFT' &&
                  $currentUserCan($permissions.PERM_DELETE_ESTABLISHMENT_INTERNAL_INVOICES)
                "
                class="mr-2"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="deleteTransfer"
              >
                <i class="fas fa-trash"></i>
                {{ $t("COMMON.DELETE") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'SUBMITTED' && !isSubmitter"
                class="mr-2"
                type="success"
                icon
                size="sm"
                :loading="loading"
                @click="accept"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_INVOICES.ACCEPT") }}
              </base-button>
              <base-button
                v-if="transfer.status === 'SUBMITTED' && !isSubmitter"
                class="mr-2"
                type="warning"
                icon
                size="sm"
                :loading="loading"
                @click="reject"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_INVOICES.REJECT") }}
              </base-button>
              <base-button
                v-if="canCancel"
                class="mr-2"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="openCancelModal"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("COMMON.CANCEL") }}
              </base-button>
              <base-button
                v-if="transfer.status !== 'DRAFT'"
                type="info"
                icon
                size="sm"
                @click="print"
              >
                <i class="fas fa-print"></i>
                {{ $t("COMMON.PRINT") }}
              </base-button>
            </h3>
          </div>
        </div>
      </template>

      <div
        class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
      >
        <tabs
          fill
          class="flex-column flex-md-row"
          tabNavWrapperClasses="nav-wrapper"
          tabNavClasses="nav nav-pills nav-fill"
          value="details"
        >
          <card shadow>
            <tab-pane title="global" id="1" :active="true">
              <span slot="title">
                <i class="ni ni-cloud-upload-96" />
                {{ $t("COMMON.GLOBAL") }}
              </span>
              <establishment-internal-invoice-view-global :transfer="transfer" />
            </tab-pane>

            <tab-pane title="details" id="2" :active="false">
              <span slot="title">
                <i class="ni ni-bullet-list-67" />
                {{ $t("COMMON.DETAILS") }}
              </span>
              <establishment-internal-invoice-items-table
                :transfer="transfer"
                @onSubmitTransferItem="handleItemSubmit"
                @onDeleteTransferItem="handleItemDelete"
                @onTransferUpdated="refreshTransfer"
              />
              <div class="row mb-5" v-if="transfer.pricing">
                <div class="col-6"></div>
                <div class="col-6">
                  <dl class="row mb-0">
                    <dt class="col-sm-6">{{ $t("COMMON.SUBTOTAL") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(transfer.pricing.subtotal || 0) }}
                    </dd>
                  </dl>

                  <dl class="row mb-0" v-if="transfer.pricing.discounts?.amount">
                    <dt class="col-sm-6">
                      {{ $t("ESTABLISHMENT_INTERNAL_INVOICES.DISCOUNT") }}
                    </dt>
                    <dd class="col-sm-6 text-right px-0 text-danger">
                      {{ $formatCurrency((transfer.pricing.discounts.amount || 0) * -1) }}
                    </dd>
                  </dl>

                  <dl class="row mb-0" v-if="transfer.pricing.taxes?.total">
                    <dt class="col-sm-6">{{ $t("COMMON.TAXES") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(transfer.pricing.taxes.total || 0) }}
                    </dd>
                    <div class="col-12">
                      <dl
                        class="row mb-0 px-0 mx-0"
                        v-for="(tax, index) in transfer.pricing.taxes.taxes"
                        :key="index"
                      >
                        <dt class="col-sm-6 pl-5 text-muted">{{ tax.label }}</dt>
                        <dd class="col-sm-6 text-right text-muted px-0">
                          {{ $formatCurrency(tax.tax_amount || 0) }}
                        </dd>
                      </dl>
                    </div>
                  </dl>

                  <dl class="row mb-0">
                    <dt class="col-sm-6">{{ $t("COMMON.TOTAL") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(transfer.pricing.total || 0) }}
                    </dd>
                  </dl>
                </div>
              </div>
            </tab-pane>
          </card>
        </tabs>
      </div>
    </card>

    <internal-document-reason-modal
      :show-modal="showCancelModal"
      :title="$t('INTERNAL_DOCUMENTS.CANCEL_INVOICE_TITLE')"
      :help="$t('INTERNAL_DOCUMENTS.CANCEL_INVOICE_HELP')"
      :reasons="cancellationReasons"
      reasons-key="CANCELLATION_REASONS"
      :confirm-label="$t('COMMON.CANCEL')"
      :loading="loading"
      @confirm="confirmCancel"
      @close="showCancelModal = false"
    />
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import defaultTransfer from "./defaultEstablishmentInternalInvoice";
import EstablishmentInternalInvoiceItemsTable from "./partials/EstablishmentInternalInvoiceItemsTable.vue";
import EstablishmentInternalInvoiceViewGlobal from "./partials/EstablishmentInternalInvoiceViewGlobal.vue";
import InternalDocumentReasonModal from "@/components/InternalDocumentReasonModal.vue";
import defaultTransferItem from "./defaultEstablishmentInternalInvoiceItem";
import { Tabs, TabPane } from "@/components";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import "flatpickr/dist/flatpickr.css";
import { openPdfBlob } from "@/helpers/downloadPdf";
import requestErrorMixin from "@/mixins/request-error-mixin";

export default {
  name: "ViewEstablishmentInternalInvoicePage",
  mixins: [requestErrorMixin],
  components: {
    EstablishmentInternalInvoiceItemsTable,
    EstablishmentInternalInvoiceViewGlobal,
    InternalDocumentReasonModal,
    Tabs,
    TabPane,
  },
  data() {
    return {
      transfer: cloneDeep(defaultTransfer),
      loading: false,
      transferItem: cloneDeep(defaultTransferItem),
      showCancelModal: false,
      cancellationReasons: [
        "WRONG_QUANTITY",
        "NOT_RECEIVED",
        "DUPLICATE",
        "WRONG_DAY",
        "OTHER",
      ],
    };
  },
  created() {
    this.getTransfer();
    if (!this.$store.getters["profile/me"]) {
      this.$store.dispatch("profile/me");
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters["profile/me"];
    },
    isSubmitter() {
      if (!this.currentUser || !this.transfer?.submittedBy) {
        return false;
      }
      return this.currentUser.id === this.transfer.submittedBy.id;
    },
    /**
     * Contester une facture, c'est l'annuler — y compris une facture déjà
     * acceptée, puisqu'une réception conforme l'est automatiquement et que
     * personne n'a alors eu l'occasion de s'y opposer. Les deux écoles en ont le
     * droit ; le serveur refuse au-delà d'un rapport hebdomadaire validé.
     */
    canCancel() {
      return ["SUBMITTED", "ACCEPTED"].includes(this.transfer.status);
    },
  },
  methods: {
    async getTransfer() {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalInvoices/get", {
          id: this.$route.params.id,
        });
        this.transfer = this.$store.getters["establishmentInternalInvoices/transfer"];
      } finally {
        this.loading = false;
      }
    },
    async refreshTransfer() {
      await this.getTransfer();
    },
    async validate() {
      if (!this.transfer.items || !this.transfer.items.length) {
        this.$notify({
          type: "danger",
          message: this.$t("ESTABLISHMENT_INTERNAL_INVOICES.ADD_ITEMS_FIRST"),
        });
        return;
      }
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalInvoices/validate", {
          id: this.transfer.id,
        });
        await this.getTransfer();
      } finally {
        this.loading = false;
      }
    },
    async accept() {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalInvoices/accept", {
          id: this.transfer.id,
        });
        await this.getTransfer();
      } finally {
        this.loading = false;
      }
    },
    async reject() {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalInvoices/reject", {
          id: this.transfer.id,
        });
        await this.getTransfer();
      } finally {
        this.loading = false;
      }
    },
    openCancelModal() {
      this.showCancelModal = true;
    },
    async confirmCancel({ reasonCode, reason }) {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalInvoices/cancel", {
          id: this.transfer.id,
          reasonCode,
          reason,
        });
        this.showCancelModal = false;
        await this.getTransfer();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    async print() {
      swal.fire({
        title: this.$t("COMMON.LOADING"),
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
      try {
        const response = await this.$store.dispatch(
          "establishmentInternalInvoices/print",
          { id: this.transfer.id }
        );
        openPdfBlob(response, `${this.transfer.seq_number || this.transfer.code}.pdf`);
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        swal.close();
      }
    },
    async handleItemSubmit(itemData) {
      if (!itemData.id) {
        await this.addTransferItem(itemData);
      } else {
        await this.updateTransferItem(itemData);
      }
      await this.refreshTransfer();
    },
    async addTransferItem(itemData) {
      const payload = {
        ...itemData,
        internalInvoice: {
          type: "establishment-internal-invoices",
          id: this.transfer.id,
        },
      };
      await this.$store.dispatch("establishmentInternalInvoiceItems/add", payload);
    },
    async updateTransferItem(itemData) {
      const payload = {
        ...itemData,
        internalInvoice: {
          type: "establishment-internal-invoices",
          id: this.transfer.id,
        },
      };
      await this.$store.dispatch("establishmentInternalInvoiceItems/update", payload);
    },
    async handleItemDelete(id) {
      await this.$store.dispatch("establishmentInternalInvoiceItems/destroy", id);
      await this.refreshTransfer();
    },
    async deleteTransfer() {
      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_INVOICES.DELETE_ESTABLISHMENT_INTERNAL_INVOICE"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) return;

      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalInvoices/destroy", {
          id: this.transfer.id,
        });
        this.$notify({
          type: "success",
          message: this.$t("ESTABLISHMENT_INTERNAL_INVOICES.DELETE_ESTABLISHMENT_INTERNAL_INVOICE"),
        });
        this.goBack();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loading = false;
      }
    },
    goToEdit() {
      this.$router.push({
        name: "Edit EstablishmentInternalInvoice",
        params: { id: this.transfer.id },
      });
    },
    goBack() {
      this.$router.push({ name: "List EstablishmentInternalInvoices" });
    },
  },
};
</script>
